import React, { useState, useEffect } from 'react';
import { 
  CheckCircle2, 
  Circle, 
  AlertCircle, 
  Loader2,
  Copy,
  ExternalLink,
  Terminal,
  Smartphone,
  Key,
  MessageSquare,
  Send,
  Shield,
  ChevronRight,
  ChevronDown,
  PlayCircle,
  BookOpen,
  Zap,
  CheckCheck,
  X,
  Info,
  HelpCircle,
  RefreshCw,
  Download,
  Clock,
  RotateCcw,
  ArrowRight,
  FileText,
  Globe,
  Sparkles
} from 'lucide-react';

interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  action?: {
    type: 'command' | 'browser' | 'input' | 'phone' | 'verify';
    command?: string;
    url?: string;
    placeholder?: string;
    expected?: string;
  };
  help?: string;
  completed: boolean;
  value?: string;
  timestamp?: Date;
  required?: string[];
  tips?: string[];
}

interface Section {
  id: string;
  title: string;
  icon: React.ElementType;
  description: string;
  items: ChecklistItem[];
  expanded: boolean;
}

export default function WhatsAppSetupTool() {
  const [sections, setSections] = useState<Section[]>([
    {
      id: 'prerequisites',
      title: 'Prerequisites',
      icon: Shield,
      description: 'What you need before starting',
      expanded: true,
      items: [
        {
          id: 'twilio-account',
          title: 'Twilio Account',
          description: 'Sign up for a free Twilio account',
          action: {
            type: 'browser',
            url: 'https://www.twilio.com/try-twilio'
          },
          help: 'Twilio provides the infrastructure for sending WhatsApp messages. The trial account includes $15 credit.',
          completed: false,
          tips: ['Use a valid email', 'Phone verification required', 'Free trial is sufficient for testing']
        },
        {
          id: 'twilio-phone',
          title: 'Twilio Phone Number',
          description: 'Get a phone number from Twilio (or use existing)',
          help: 'You can use any Twilio phone number for SMS. For WhatsApp, we\'ll use the sandbox.',
          completed: false
        },
        {
          id: 'project-directory',
          title: 'Project Directory',
          description: 'Ensure you\'re in the correct project directory',
          action: {
            type: 'command',
            command: 'pwd',
            expected: '/Users/brianball/Code/biig-biz'
          },
          completed: false
        }
      ]
    },
    {
      id: 'credentials',
      title: 'Get Twilio Credentials',
      icon: Key,
      description: 'Retrieve your API credentials from Twilio',
      expanded: false,
      items: [
        {
          id: 'open-console',
          title: 'Open Twilio Console',
          description: 'Navigate to your Twilio dashboard',
          action: {
            type: 'browser',
            url: 'https://console.twilio.com'
          },
          completed: false
        },
        {
          id: 'account-sid',
          title: 'Copy Account SID',
          description: 'Find your Account SID (starts with AC)',
          action: {
            type: 'input',
            placeholder: 'AC...(34 characters total)'
          },
          help: 'Located on the Twilio Console homepage. Starts with "AC" followed by 32 alphanumeric characters.',
          completed: false,
          tips: ['Don\'t share this publicly', 'Different for each account', 'Case-sensitive']
        },
        {
          id: 'auth-token',
          title: 'Copy Auth Token',
          description: 'Get your Auth Token (keep it secret!)',
          action: {
            type: 'input',
            placeholder: '32 character auth token'
          },
          help: 'Click "Show" next to Auth Token on the dashboard. This is your secret API key.',
          completed: false,
          tips: ['Never commit to git', 'Regenerate if compromised', 'Required for API calls']
        }
      ]
    },
    {
      id: 'cloudflare',
      title: 'Configure Cloudflare Worker',
      icon: Globe,
      description: 'Add secrets to your Cloudflare Worker',
      expanded: false,
      items: [
        {
          id: 'cd-api',
          title: 'Navigate to API Directory',
          description: 'Change to the API directory',
          action: {
            type: 'command',
            command: 'cd apps/api'
          },
          completed: false
        },
        {
          id: 'add-sid',
          title: 'Add Account SID Secret',
          description: 'Store Account SID in Cloudflare',
          action: {
            type: 'command',
            command: 'npx wrangler secret put TWILIO_ACCOUNT_SID',
            expected: '✨ Success! Uploaded secret TWILIO_ACCOUNT_SID'
          },
          help: 'Paste your Account SID when prompted, then press Enter',
          required: ['account-sid'],
          completed: false
        },
        {
          id: 'add-token',
          title: 'Add Auth Token Secret',
          description: 'Store Auth Token in Cloudflare',
          action: {
            type: 'command',
            command: 'npx wrangler secret put TWILIO_AUTH_TOKEN',
            expected: '✨ Success! Uploaded secret TWILIO_AUTH_TOKEN'
          },
          help: 'Paste your Auth Token when prompted, then press Enter',
          required: ['auth-token'],
          completed: false
        },
        {
          id: 'add-whatsapp-number',
          title: 'Add WhatsApp Number Secret',
          description: 'Store WhatsApp sandbox number',
          action: {
            type: 'command',
            command: 'npx wrangler secret put TWILIO_WHATSAPP_NUMBER',
            expected: '✨ Success! Uploaded secret TWILIO_WHATSAPP_NUMBER'
          },
          help: 'Type exactly: whatsapp:+14155238886',
          completed: false,
          tips: ['Must include "whatsapp:" prefix', 'Sandbox number is always +14155238886']
        }
      ]
    },
    {
      id: 'whatsapp-setup',
      title: 'Setup WhatsApp Sandbox',
      icon: MessageSquare,
      description: 'Connect your phone to Twilio\'s WhatsApp sandbox',
      expanded: false,
      items: [
        {
          id: 'open-sandbox',
          title: 'Open WhatsApp Sandbox Page',
          description: 'Navigate to Twilio\'s WhatsApp sandbox',
          action: {
            type: 'browser',
            url: 'https://console.twilio.com/us1/develop/sms/try-it-out/whatsapp-learn'
          },
          completed: false
        },
        {
          id: 'find-join-code',
          title: 'Find Your Join Code',
          description: 'Locate your unique sandbox keyword',
          action: {
            type: 'input',
            placeholder: 'join word-word'
          },
          help: 'Look for text like "Send join famous-cloud to +14155238886". Each account has a unique code.',
          completed: false,
          tips: ['Code is unique per account', 'Format: "join word-word"', 'Case-sensitive']
        },
        {
          id: 'add-contact',
          title: 'Add Twilio to WhatsApp',
          description: 'Save Twilio sandbox as a contact',
          action: {
            type: 'phone'
          },
          help: 'Add contact: +1 415 523 8886, Name: "Twilio Sandbox"',
          completed: false
        },
        {
          id: 'send-join',
          title: 'Send Join Message',
          description: 'Send your join code to activate sandbox',
          action: {
            type: 'phone'
          },
          help: 'Send your join code (e.g., "join famous-cloud") to the Twilio contact',
          required: ['find-join-code', 'add-contact'],
          completed: false,
          tips: ['Must match exactly', 'You\'ll get confirmation message', 'Valid for 3 days']
        }
      ]
    },
    {
      id: 'deploy-test',
      title: 'Deploy & Test',
      icon: Zap,
      description: 'Deploy your API and test WhatsApp messaging',
      expanded: false,
      items: [
        {
          id: 'deploy-api',
          title: 'Deploy API to Cloudflare',
          description: 'Push your API with configured secrets',
          action: {
            type: 'command',
            command: 'npm run deploy',
            expected: 'Published biig-api'
          },
          required: ['add-sid', 'add-token', 'add-whatsapp-number'],
          completed: false
        },
        {
          id: 'verify-secrets',
          title: 'Verify Secrets Configuration',
          description: 'Check that all secrets are properly set',
          action: {
            type: 'verify',
            command: 'curl https://biig-api.b-9f2.workers.dev/api/dev/env',
            expected: 'smsReady: true'
          },
          help: 'Click "Run Verification" to check if all secrets are configured correctly',
          completed: false
        },
        {
          id: 'test-number',
          title: 'Enter Test Phone Number',
          description: 'Your WhatsApp number with country code',
          action: {
            type: 'input',
            placeholder: '+12125551234'
          },
          help: 'Include country code (e.g., +1 for US, +55 for Brazil)',
          completed: false
        },
        {
          id: 'send-test',
          title: 'Send Test Message',
          description: 'Send your first WhatsApp message!',
          action: {
            type: 'verify'
          },
          required: ['test-number', 'send-join'],
          completed: false
        }
      ]
    }
  ]);

  const [currentTip, setCurrentTip] = useState<string>('');
  const [copiedCommand, setCopiedCommand] = useState<string>('');
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('Hello from WhatsApp Setup Tool! 🎉');
  const [sending, setSending] = useState(false);
  const [testResult, setTestResult] = useState<{success: boolean; message: string} | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [verificationResults, setVerificationResults] = useState<Record<string, any>>({});

  // Check configuration status on mount
  useEffect(() => {
    checkConfigurationStatus();
  }, []);

  // Load saved progress
  useEffect(() => {
    const saved = localStorage.getItem('whatsapp-setup-progress');
    if (saved) {
      try {
        const progress = JSON.parse(saved);
        setSections(prev => prev.map(section => ({
          ...section,
          items: section.items.map(item => ({
            ...item,
            completed: progress[item.id]?.completed || false,
            value: progress[item.id]?.value || item.value,
            timestamp: progress[item.id]?.timestamp ? new Date(progress[item.id].timestamp) : undefined
          }))
        })));
      } catch (e) {
        console.error('Failed to load progress:', e);
      }
    }
  }, []);

  // Save progress
  const saveProgress = (sections: Section[]) => {
    const progress: Record<string, any> = {};
    sections.forEach(section => {
      section.items.forEach(item => {
        progress[item.id] = {
          completed: item.completed,
          value: item.value,
          timestamp: item.timestamp
        };
      });
    });
    localStorage.setItem('whatsapp-setup-progress', JSON.stringify(progress));
  };

  const toggleSection = (sectionId: string) => {
    setSections(prev => prev.map(section => 
      section.id === sectionId 
        ? { ...section, expanded: !section.expanded }
        : section
    ));
  };

  const toggleItem = (sectionId: string, itemId: string, value?: string) => {
    setSections(prev => {
      const updated = prev.map(section => 
        section.id === sectionId 
          ? {
              ...section,
              items: section.items.map(item =>
                item.id === itemId
                  ? { 
                      ...item, 
                      completed: !item.completed,
                      value: value || item.value,
                      timestamp: !item.completed ? new Date() : item.timestamp
                    }
                  : item
              )
            }
          : section
      );
      saveProgress(updated);
      return updated;
    });
  };

  const updateItemValue = (sectionId: string, itemId: string, value: string) => {
    setSections(prev => {
      const updated = prev.map(section => 
        section.id === sectionId 
          ? {
              ...section,
              items: section.items.map(item =>
                item.id === itemId
                  ? { ...item, value }
                  : item
              )
            }
          : section
      );
      saveProgress(updated);
      return updated;
    });
  };

  const copyCommand = async (command: string) => {
    await navigator.clipboard.writeText(command);
    setCopiedCommand(command);
    setTimeout(() => setCopiedCommand(''), 2000);
  };

  const resetProgress = () => {
    if (confirm('This will reset all your progress. Are you sure?')) {
      setSections(prev => prev.map(section => ({
        ...section,
        items: section.items.map(item => ({
          ...item,
          completed: false,
          value: undefined,
          timestamp: undefined
        }))
      })));
      localStorage.removeItem('whatsapp-setup-progress');
    }
  };

  const checkConfigurationStatus = async () => {
    setVerifying(true);
    try {
      // Check if API is deployed and secrets are configured
      const response = await fetch('https://biig-api.b-9f2.workers.dev/api/dev/env');
      if (response.ok) {
        const data = await response.json();
        
        // Auto-check items based on configuration
        setSections(prev => prev.map(section => {
          if (section.id === 'cloudflare') {
            return {
              ...section,
              items: section.items.map(item => {
                if (item.id === 'add-sid' && data.twilioConfigured?.accountSid) {
                  return { ...item, completed: true, timestamp: new Date() };
                }
                if (item.id === 'add-token' && data.twilioConfigured?.authToken) {
                  return { ...item, completed: true, timestamp: new Date() };
                }
                if (item.id === 'add-whatsapp-number' && data.twilioConfigured?.whatsappNumber !== 'NOT SET') {
                  return { ...item, completed: true, timestamp: new Date() };
                }
                return item;
              })
            };
          }
          if (section.id === 'deploy-test') {
            return {
              ...section,
              items: section.items.map(item => {
                if (item.id === 'deploy-api' && data.status === 'Development Mode') {
                  return { ...item, completed: true, timestamp: new Date() };
                }
                if (item.id === 'verify-secrets' && data.smsReady === true) {
                  return { ...item, completed: true, timestamp: new Date() };
                }
                return item;
              })
            };
          }
          return section;
        }));
        
        setVerificationResults(data);
      }
    } catch (error) {
      console.error('Failed to check configuration:', error);
    } finally {
      setVerifying(false);
    }
  };

  const runVerification = async (itemId: string) => {
    setVerifying(true);
    try {
      if (itemId === 'verify-secrets') {
        const response = await fetch('https://biig-api.b-9f2.workers.dev/api/dev/env');
        if (response.ok) {
          const data = await response.json();
          const isReady = data.smsReady === true;
          
          toggleItem('deploy-test', 'verify-secrets');
          
          setTestResult({
            success: isReady,
            message: isReady 
              ? '✅ All secrets are properly configured!' 
              : '❌ Secrets are not fully configured. Check the missing items above.'
          });
          
          return isReady;
        }
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Failed to verify configuration. Is your API deployed?'
      });
    } finally {
      setVerifying(false);
    }
    return false;
  };

  const sendTestMessage = async () => {
    if (!testPhone || !testMessage) {
      setTestResult({ success: false, message: 'Please enter phone number and message' });
      return;
    }

    setSending(true);
    setTestResult(null);

    try {
      const response = await fetch('https://biig-api.b-9f2.workers.dev/api/whatsapp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: testPhone,
          message: testMessage
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        setTestResult({ success: true, message: 'Message sent successfully! Check your WhatsApp.' });
        toggleItem('deploy-test', 'send-test');
      } else {
        setTestResult({ 
          success: false, 
          message: result.error || 'Failed to send message. Check your configuration.' 
        });
      }
    } catch (error) {
      setTestResult({ 
        success: false, 
        message: 'Network error. Is your API deployed?' 
      });
    } finally {
      setSending(false);
    }
  };

  const allItemsCompleted = sections.every(section => 
    section.items.every(item => item.completed)
  );

  const completedCount = sections.reduce((total, section) => 
    total + section.items.filter(item => item.completed).length, 0
  );

  const totalCount = sections.reduce((total, section) => 
    total + section.items.length, 0
  );

  const progressPercentage = (completedCount / totalCount) * 100;

  const canComplete = (item: ChecklistItem, section: Section) => {
    if (!item.required) return true;
    return item.required.every(reqId => {
      const allItems = sections.flatMap(s => s.items);
      return allItems.find(i => i.id === reqId)?.completed;
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <MessageSquare className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">WhatsApp Setup Tool</h1>
                <p className="text-sm text-gray-600">Complete interactive setup guide & testing tool</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={checkConfigurationStatus}
                disabled={verifying}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {verifying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    Check Status
                  </>
                )}
              </button>
              <button
                onClick={resetProgress}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 flex items-center gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Reset
              </button>
              <div className="text-sm text-gray-500">
                {completedCount} / {totalCount} completed
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-green-500 to-blue-500 transition-all duration-500"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-3 gap-8">
          {/* Main Checklist */}
          <div className="col-span-2 space-y-4">
            {sections.map((section) => {
              const SectionIcon = section.icon;
              const sectionCompleted = section.items.every(item => item.completed);
              
              return (
                <div 
                  key={section.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                >
                  <button
                    onClick={() => toggleSection(section.id)}
                    className={`w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors ${
                      sectionCompleted ? 'bg-green-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        sectionCompleted ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        <SectionIcon className={`h-5 w-5 ${
                          sectionCompleted ? 'text-green-600' : 'text-gray-600'
                        }`} />
                      </div>
                      <div className="text-left">
                        <h2 className="font-semibold text-gray-900">{section.title}</h2>
                        <p className="text-sm text-gray-600">{section.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">
                        {section.items.filter(i => i.completed).length} / {section.items.length}
                      </span>
                      {section.expanded ? (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </button>

                  {section.expanded && (
                    <div className="border-t border-gray-200">
                      {section.items.map((item, index) => {
                        const canCheck = canComplete(item, section);
                        
                        return (
                          <div 
                            key={item.id}
                            className={`px-6 py-4 ${
                              index < section.items.length - 1 ? 'border-b border-gray-100' : ''
                            } ${!canCheck ? 'opacity-50' : ''}`}
                          >
                            <div className="flex items-start gap-3">
                              <button
                                onClick={() => canCheck && toggleItem(section.id, item.id)}
                                disabled={!canCheck}
                                className="mt-1"
                              >
                                {item.completed ? (
                                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                                ) : (
                                  <Circle className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                )}
                              </button>
                              
                              <div className="flex-1">
                                <h3 className="font-medium text-gray-900">{item.title}</h3>
                                <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                                
                                {item.help && (
                                  <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                                    <div className="flex items-start gap-2">
                                      <Info className="h-4 w-4 text-blue-500 mt-0.5" />
                                      <p className="text-sm text-blue-700">{item.help}</p>
                                    </div>
                                  </div>
                                )}

                                {item.action && (
                                  <div className="mt-3">
                                    {item.action.type === 'command' && (
                                      <div className="flex items-center gap-2">
                                        <div className="flex-1 bg-gray-900 text-green-400 p-3 rounded-lg font-mono text-sm">
                                          $ {item.action.command}
                                        </div>
                                        <button
                                          onClick={() => item.action?.command && copyCommand(item.action.command)}
                                          className="p-2 text-gray-600 hover:text-gray-900"
                                        >
                                          {copiedCommand === item.action.command ? (
                                            <CheckCheck className="h-5 w-5 text-green-500" />
                                          ) : (
                                            <Copy className="h-5 w-5" />
                                          )}
                                        </button>
                                      </div>
                                    )}

                                    {item.action.type === 'browser' && (
                                      <a
                                        href={item.action.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                      >
                                        <ExternalLink className="h-4 w-4" />
                                        Open in Browser
                                      </a>
                                    )}

                                    {item.action.type === 'input' && (
                                      <input
                                        type="text"
                                        placeholder={item.action.placeholder}
                                        value={item.value || ''}
                                        onChange={(e) => updateItemValue(section.id, item.id, e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                      />
                                    )}

                                    {item.action.type === 'phone' && (
                                      <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <Smartphone className="h-4 w-4" />
                                        <span>Complete this step on your phone</span>
                                      </div>
                                    )}

                                    {item.action.type === 'verify' && (
                                      <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                          <div className="flex-1 bg-gray-900 text-green-400 p-3 rounded-lg font-mono text-sm">
                                            $ {item.action.command}
                                          </div>
                                          <button
                                            onClick={() => item.action?.command && copyCommand(item.action.command)}
                                            className="p-2 text-gray-600 hover:text-gray-900"
                                          >
                                            {copiedCommand === item.action.command ? (
                                              <CheckCheck className="h-5 w-5 text-green-500" />
                                            ) : (
                                              <Copy className="h-5 w-5" />
                                            )}
                                          </button>
                                        </div>
                                        <button
                                          onClick={() => runVerification(item.id)}
                                          disabled={verifying}
                                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                                        >
                                          {verifying ? (
                                            <>
                                              <Loader2 className="h-4 w-4 animate-spin" />
                                              Verifying...
                                            </>
                                          ) : (
                                            <>
                                              <PlayCircle className="h-4 w-4" />
                                              Run Verification
                                            </>
                                          )}
                                        </button>
                                      </div>
                                    )}

                                    {item.action.expected && (
                                      <div className="mt-2 text-sm text-gray-600">
                                        <span className="font-medium">Expected result:</span> {item.action.expected}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {item.tips && item.tips.length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    {item.tips.map((tip, i) => (
                                      <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                                        <Sparkles className="h-3 w-3 text-yellow-500 mt-0.5" />
                                        <span>{tip}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {item.timestamp && (
                                  <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                                    <Clock className="h-3 w-3" />
                                    Completed at {item.timestamp.toLocaleTimeString()}
                                  </div>
                                )}

                                {!canCheck && item.required && (
                                  <div className="mt-2 text-sm text-orange-600">
                                    ⚠️ Complete required steps first
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Side Panel */}
          <div className="space-y-4">
            {/* Configuration Status */}
            {verificationResults && Object.keys(verificationResults).length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Configuration Status
                </h3>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Account SID</span>
                    {verificationResults.twilioConfigured?.accountSid ? (
                      <span className="text-green-600 font-medium">✅ Configured</span>
                    ) : (
                      <span className="text-red-600 font-medium">❌ Missing</span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Auth Token</span>
                    {verificationResults.twilioConfigured?.authToken ? (
                      <span className="text-green-600 font-medium">✅ Configured</span>
                    ) : (
                      <span className="text-red-600 font-medium">❌ Missing</span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">WhatsApp Number</span>
                    {verificationResults.twilioConfigured?.whatsappNumber !== 'NOT SET' ? (
                      <span className="text-green-600 font-medium">✅ {verificationResults.twilioConfigured?.whatsappNumber}</span>
                    ) : (
                      <span className="text-red-600 font-medium">❌ Not Set</span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">SMS Ready</span>
                    {verificationResults.smsReady ? (
                      <span className="text-green-600 font-medium">✅ Ready</span>
                    ) : (
                      <span className="text-orange-600 font-medium">⚠️ Not Ready</span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">API Status</span>
                    <span className="text-blue-600 font-medium">{verificationResults.status || 'Unknown'}</span>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600">
                    Last checked: {new Date().toLocaleTimeString()}
                  </p>
                </div>
              </div>
            )}

            {/* Test Message Sender */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Send className="h-5 w-5" />
                Test WhatsApp Message
              </h3>
              
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Phone Number</label>
                  <input
                    type="text"
                    placeholder="+1234567890"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Message</label>
                  <textarea
                    placeholder="Your message..."
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    rows={3}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <button
                  onClick={sendTestMessage}
                  disabled={sending || !testPhone || !testMessage}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {sending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Send Test Message
                    </>
                  )}
                </button>

                {testResult && (
                  <div className={`p-3 rounded-lg text-sm ${
                    testResult.success 
                      ? 'bg-green-50 text-green-700 border border-green-200' 
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    {testResult.message}
                  </div>
                )}
              </div>
            </div>

            {/* Quick Commands */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Terminal className="h-5 w-5" />
                Quick Commands
              </h3>
              
              <div className="space-y-2">
                <button
                  onClick={() => copyCommand('npx wrangler secret list')}
                  className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm font-mono"
                >
                  List secrets
                </button>
                <button
                  onClick={() => copyCommand('npm run deploy')}
                  className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm font-mono"
                >
                  Deploy API
                </button>
                <button
                  onClick={() => copyCommand('curl https://biig-api.b-9f2.workers.dev/api/health')}
                  className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm font-mono"
                >
                  Check API health
                </button>
              </div>
            </div>

            {/* Resources */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Resources
              </h3>
              
              <div className="space-y-2">
                <a 
                  href="https://www.twilio.com/docs/whatsapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                >
                  <ExternalLink className="h-4 w-4" />
                  Twilio WhatsApp Docs
                </a>
                <a 
                  href="https://console.twilio.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                >
                  <ExternalLink className="h-4 w-4" />
                  Twilio Console
                </a>
                <a 
                  href="https://developers.cloudflare.com/workers/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                >
                  <ExternalLink className="h-4 w-4" />
                  Cloudflare Workers Docs
                </a>
              </div>
            </div>

            {/* Success Badge */}
            {allItemsCompleted && (
              <div className="bg-gradient-to-r from-green-500 to-blue-500 rounded-lg p-6 text-white">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle2 className="h-8 w-8" />
                  <h3 className="text-xl font-bold">Setup Complete!</h3>
                </div>
                <p className="text-sm opacity-90">
                  You've successfully configured WhatsApp messaging. Your app can now send messages to any WhatsApp number that has joined your sandbox.
                </p>
                <div className="mt-4 p-3 bg-white/20 rounded-lg">
                  <p className="text-xs font-semibold mb-1">Next Steps:</p>
                  <p className="text-xs">Apply for WhatsApp Business API access for production use without sandbox limitations.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}